import type { AzureSuggestResponse } from "./search-suggest.types";

/**
 * The two suggester calls, as the published site search makes them.
 *
 * ⚠ CALLED FROM THE BROWSER, SAME-ORIGIN. There is no server hop: a suggest
 * fires on every debounced keystroke, and routing that through our pods buys
 * nothing the suggesters do not already do themselves.
 */

/** Asking for more than we show is deliberate — see MAX_SUGGESTIONS. */
export const UPSTREAM_LIMIT = 15;
export const API_VERSION = "2020-06-30";

/**
 * AC-9's eight, split the way the production search splits it: support is
 * capped at 3 and products fill whatever support leaves. Support never
 * backfills a thin product page — that cap is production's. Dedupe-by-title
 * and the safety gate both run before the cap, so asking upstream for only
 * eight would render fewer than eight whenever a page is thin.
 */
export const MAX_SUGGESTIONS = 8;
export const MAX_SUPPORT_SUGGESTIONS = 3;

/** One upstream suggest call, as the published site search makes it. */
export interface Suggester {
  /** Same-origin route this leg posts to. */
  readonly path: "product-suggest" | "support-suggest";
  /** Fields asked of the index — the adapter reads nothing it does not select. */
  readonly select: string;
  /** The live Azure index backing this leg. */
  readonly suggesterName: string;
  /** OData filter sent with every call, or null to ask unfiltered. */
  readonly filter: string | null;
}

/** Bodies transcribed from the published search component: the suggester names
 *  are the live Azure indexes. The product select adds `url`, which the adapter
 *  links from and the published body leaves out. */
export const PRODUCT: Suggester = {
  path: "product-suggest",
  select: "title,id,url,lang,description",
  suggesterName: "idx-prd-product",
  filter: "lang eq 'en'"
};

export const SUPPORT: Suggester = {
  path: "support-suggest",
  select: "title,publishedId,contentCategory,service,description",
  suggesterName: "idx-prd-supp",
  filter: "contentCategory ne 'video'"
};

/**
 * One suggester. Resolves with the raw body, or throws — the caller decides
 * what a single failed leg means.
 *
 * ⚠ A 200 without a `value` array (a WAF envelope, `null`) is a FAILURE, not
 * "nothing matched": as `[]` it would paint the empty-results message and
 * report a zero-result search.
 */
export async function requestSuggestions(
  base: string,
  suggester: Suggester,
  term: string,
  signal: AbortSignal
): Promise<AzureSuggestResponse<unknown>> {
  const url =
    `${base}/${suggester.path}` +
    `?api-version=${API_VERSION}&limit=${UPSTREAM_LIMIT}&search=${encodeURIComponent(term)}`;
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      select: suggester.select,
      suggesterName: suggester.suggesterName,
      search: term,
      filter: suggester.filter
    }),
    signal
  });
  if (!response.ok) {
    const failure = new Error(`${suggester.path} responded ${response.status}`);
    /* The caller reports the status as `errorCode`; the message cannot carry
       it because the message is never shown and may be replaced. */
    (failure as Error & { status?: string }).status = String(response.status);
    throw failure;
  }
  /* A parse error's message can quote the body, and the body can echo the
     query, so it is replaced rather than propagated. */
  let body: AzureSuggestResponse<unknown>;
  try {
    body = await response.json();
  } catch {
    throw new Error(`${suggester.path} returned an unreadable body`);
  }
  if (!Array.isArray(body?.value)) {
    throw new Error(`${suggester.path} answered without a value array`);
  }
  return body;
}
